// Compute the set of CSS custom properties for a public booking page from the
// attraction's branding, layered over the selected template. Only emits a variable
// when the attraction has explicitly set a value — otherwise the template's own CSS
// defaults (defined in index.css as --d-* vars / template rules) remain active.
import { getTemplate, mergeBranding } from "./templates";
import { fontStack } from "./fonts";

function hexToRgba(hex, opacityPct) {
  const c = String(hex || "").replace("#", "");
  if (c.length !== 6) return null;
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const a = opacityPct == null ? 1 : Math.max(0, Math.min(100, opacityPct)) / 100;
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

const has = (v) => v !== undefined && v !== null && v !== "";

// Returns { vars: {..cssCustomProps}, fonts: [familyNamesToLoad] }
export function brandingCssVars(branding) {
  const b = mergeBranding(branding);
  const t = getTemplate(b.template_id || "default");
  const vars = {};
  const fonts = [];

  // ── Core colours (always set so inline colours win consistently) ──
  vars["--accent-color"] = b.accent_color || t.accent;
  vars["--bg-color"] = b.bg_color || t.bg;
  vars["--text-color"] = b.text_color || t.text;

  // ── Fonts (per text element; attraction choice overrides the template) ──
  const titleFont = b.title_font || b.heading_font; // heading_font = legacy (Update 13)
  if (has(titleFont)) { const s = fontStack(titleFont); vars["--title-font"] = s; vars["--heading-font"] = s; fonts.push(titleFont); }
  else { vars["--title-font"] = t.headingFont; vars["--heading-font"] = t.headingFont; }
  if (has(b.subtitle_font)) { vars["--subtitle-font"] = fontStack(b.subtitle_font); fonts.push(b.subtitle_font); }
  else vars["--subtitle-font"] = t.bodyFont;
  if (has(b.body_font)) { vars["--body-font"] = fontStack(b.body_font); fonts.push(b.body_font); }
  else vars["--body-font"] = t.bodyFont;

  // ── Logo display size + alignment ──
  if (has(b.logo_size)) vars["--logo-size"] = `${b.logo_size}px`;
  vars["--logo-align"] = b.logo_align || (["carnival", "crypt"].includes(t.id) ? "center" : "left");

  // ── Background image + opacity overlay (Update 15: colour is base, image layers on top) ──
  const bgImage = b.bg_image_url || t.bgImage;
  if (has(bgImage)) {
    vars["--bg-image"] = `url(${bgImage})`;
    const op = b.bg_image_opacity != null ? b.bg_image_opacity : 80;
    vars["--bg-image-opacity-value"] = String(Math.max(0, Math.min(100, Number(op))) / 100);
  } else {
    vars["--bg-image-opacity-value"] = "0";
  }

  // ── Header container (Update 16/17) ──
  if (has(b.header_bg_color)) vars["--header-bg-color"] = hexToRgba(b.header_bg_color, b.header_bg_opacity == null ? 100 : b.header_bg_opacity) || b.header_bg_color;
  if (has(b.header_bg_image_url)) vars["--header-bg-image"] = `url(${b.header_bg_image_url})`;
  if (has(b.header_border_color)) vars["--header-border-color"] = b.header_border_color;
  if (has(b.header_border_width)) vars["--header-border-width"] = `${b.header_border_width}px`;

  // ── Title & subtitle ──
  if (has(b.title_size)) vars["--title-size"] = `${b.title_size}px`;
  if (has(b.title_weight)) vars["--title-weight"] = String(b.title_weight);
  if (has(b.title_color)) vars["--title-color"] = b.title_color;
  if (has(b.title_transform)) vars["--title-transform"] = b.title_transform;
  if (has(b.title_letter_spacing)) vars["--title-letter-spacing"] = `${b.title_letter_spacing}px`;
  if (has(b.title_align)) vars["--title-align"] = b.title_align;
  if (has(b.title_padding_top)) vars["--title-padding-top"] = `${b.title_padding_top}px`;
  if (has(b.title_padding_bottom)) vars["--title-padding-bottom"] = `${b.title_padding_bottom}px`;
  if (has(b.subtitle_size)) vars["--subtitle-size"] = `${b.subtitle_size}px`;
  if (has(b.subtitle_weight)) vars["--subtitle-weight"] = String(b.subtitle_weight);
  if (has(b.subtitle_color)) vars["--subtitle-color"] = b.subtitle_color;
  if (has(b.subtitle_align)) vars["--subtitle-align"] = b.subtitle_align;
  if (has(b.subtitle_padding_top)) vars["--subtitle-padding-top"] = `${b.subtitle_padding_top}px`;
  if (has(b.subtitle_padding_bottom)) vars["--subtitle-padding-bottom"] = `${b.subtitle_padding_bottom}px`;

  // ── Welcome / intro text ──
  if (has(b.welcome_font)) { vars["--welcome-font"] = fontStack(b.welcome_font); fonts.push(b.welcome_font); }
  if (has(b.welcome_size)) vars["--welcome-size"] = `${b.welcome_size}px`;
  if (has(b.welcome_weight)) vars["--welcome-weight"] = String(b.welcome_weight);
  if (has(b.welcome_color)) vars["--welcome-color"] = b.welcome_color;
  if (has(b.welcome_align)) vars["--welcome-align"] = b.welcome_align;
  if (has(b.welcome_padding_top)) vars["--welcome-padding-top"] = `${b.welcome_padding_top}px`;
  if (has(b.welcome_padding_bottom)) vars["--welcome-padding-bottom"] = `${b.welcome_padding_bottom}px`;

  // ── Box heading fonts (Update 25: calendar box + summary box titles) ──
  if (has(b.cal_heading_font)) { vars["--cal-heading-font"] = fontStack(b.cal_heading_font); fonts.push(b.cal_heading_font); }
  if (has(b.summary_heading_font)) { vars["--summary-heading-font"] = fontStack(b.summary_heading_font); fonts.push(b.summary_heading_font); }

  // ── Calendar cell styling (Update 15) ──
  if (has(b.cell_bg)) { const v = hexToRgba(b.cell_bg, b.cell_bg_opacity); if (v) vars["--cell-bg"] = v; }
  if (has(b.cell_font)) { vars["--cell-font"] = fontStack(b.cell_font); fonts.push(b.cell_font); }
  if (has(b.cell_font_size)) vars["--cell-font-size"] = `${b.cell_font_size}px`;
  if (has(b.cell_font_color)) vars["--cell-font-color"] = b.cell_font_color;
  if (has(b.cell_border_color)) {
    const w = has(b.cell_border_width) ? b.cell_border_width : 1;
    const s = b.cell_border_style || "solid";
    vars["--cell-border"] = `${w}px ${s} ${b.cell_border_color}`;
  } else if (b.cell_border_style === "none") {
    vars["--cell-border"] = "none";
  }
  if (has(b.cell_border_radius)) vars["--cell-radius"] = `${b.cell_border_radius}px`;
  if (has(b.cell_open_bg)) { const v = hexToRgba(b.cell_open_bg, b.cell_open_bg_opacity); if (v) vars["--cell-open-bg"] = v; }
  if (has(b.cell_open_font_color)) vars["--cell-open-font-color"] = b.cell_open_font_color;
  if (has(b.cell_open_border_color)) vars["--cell-open-border-color"] = b.cell_open_border_color;
  if (has(b.cell_selected_bg)) vars["--cell-selected-bg"] = b.cell_selected_bg;
  if (has(b.cell_selected_font_color)) vars["--cell-selected-font-color"] = b.cell_selected_font_color;

  // ── Calendar box ──
  if (has(b.cal_box_bg)) { const v = hexToRgba(b.cal_box_bg, b.cal_box_bg_opacity); if (v) vars["--cal-box-bg"] = v; }
  if (has(b.cal_box_border_color)) {
    const w = has(b.cal_box_border_width) ? b.cal_box_border_width : 1;
    const s = b.cal_box_border_style || "solid";
    vars["--cal-box-border"] = `${w}px ${s} ${b.cal_box_border_color}`;
  } else if (b.cal_box_border_style === "none") {
    vars["--cal-box-border"] = "none";
  }
  if (has(b.cal_box_border_radius)) vars["--cal-box-radius"] = `${b.cal_box_border_radius}px`;
  if (has(b.cal_box_padding)) vars["--cal-box-padding"] = `${b.cal_box_padding}px`;

  // ── Booking (info) panel ──
  if (has(b.panel_bg)) { const v = hexToRgba(b.panel_bg, b.panel_bg_opacity); if (v) vars["--panel-bg"] = v; }
  if (has(b.panel_border_color)) {
    const w = has(b.panel_border_width) ? b.panel_border_width : 1;
    const s = b.panel_border_style || "solid";
    vars["--panel-border"] = `${w}px ${s} ${b.panel_border_color}`;
  } else if (b.panel_border_style === "none") {
    vars["--panel-border"] = "none";
  }
  if (has(b.panel_border_radius)) vars["--panel-radius"] = `${b.panel_border_radius}px`;
  if (has(b.panel_padding)) vars["--panel-padding"] = `${b.panel_padding}px`;

  // ── Buttons ──
  if (has(b.btn_bg)) vars["--btn-bg"] = b.btn_bg;
  if (has(b.btn_text_color)) vars["--btn-text"] = b.btn_text_color;
  if (has(b.btn_border_radius)) vars["--btn-radius"] = `${b.btn_border_radius}px`;
  if (has(b.btn_font_size)) vars["--btn-font-size"] = `${b.btn_font_size}px`;
  if (has(b.btn_font_weight)) vars["--btn-font-weight"] = String(b.btn_font_weight);
  if (has(b.btn_text_transform)) vars["--btn-transform"] = b.btn_text_transform;
  if (has(b.btn_letter_spacing)) vars["--btn-spacing"] = `${b.btn_letter_spacing}px`;

  // ── Time slot item styling (Update 17) ──
  if (has(b.slot_item_bg)) { const v = hexToRgba(b.slot_item_bg, b.slot_item_bg_opacity); if (v) vars["--slot-bg"] = v; }
  if (has(b.slot_item_font)) { vars["--slot-font"] = fontStack(b.slot_item_font); fonts.push(b.slot_item_font); }
  if (has(b.slot_item_border_color)) {
    const w = has(b.slot_item_border_width) ? b.slot_item_border_width : 1;
    const s = b.slot_item_border_style || "solid";
    vars["--slot-border"] = `${w}px ${s} ${b.slot_item_border_color}`;
  } else if (b.slot_item_border_style === "none") {
    vars["--slot-border"] = "none";
  }
  if (has(b.slot_item_border_radius)) vars["--slot-radius"] = `${b.slot_item_border_radius}px`;
  if (has(b.slot_item_font_size)) vars["--slot-fs"] = `${b.slot_item_font_size}px`;
  if (has(b.slot_item_font_color)) vars["--slot-fc"] = b.slot_item_font_color;
  if (has(b.slot_item_selected_bg)) vars["--slot-selected-bg"] = b.slot_item_selected_bg;
  if (has(b.slot_item_selected_font_color)) vars["--slot-selected-fc"] = b.slot_item_selected_font_color;

  // ── Time slot container (Update 17 — null = inherit calendar box) ──
  if (has(b.timeslot_container_bg)) { const v = hexToRgba(b.timeslot_container_bg, b.timeslot_container_bg_opacity); if (v) vars["--timeslot-container-bg"] = v; }
  if (has(b.timeslot_container_border_color)) {
    const w = has(b.timeslot_container_border_width) ? b.timeslot_container_border_width : 1;
    const s = b.timeslot_container_border_style || "solid";
    vars["--timeslot-container-border"] = `${w}px ${s} ${b.timeslot_container_border_color}`;
  } else if (b.timeslot_container_border_style === "none") {
    vars["--timeslot-container-border"] = "none";
  }
  if (has(b.timeslot_container_radius)) vars["--timeslot-container-radius"] = `${b.timeslot_container_radius}px`;
  if (has(b.timeslot_container_padding)) vars["--timeslot-container-padding"] = `${b.timeslot_container_padding}px`;

  // ── Ticket / add-on item styling (Update 18) ──
  if (has(b.ticket_item_bg)) { const v = hexToRgba(b.ticket_item_bg, b.ticket_item_bg_opacity); if (v) vars["--ticket-bg"] = v; }
  if (has(b.ticket_item_border_color)) {
    const w = has(b.ticket_item_border_width) ? b.ticket_item_border_width : 1;
    const s = b.ticket_item_border_style || "solid";
    vars["--ticket-border"] = `${w}px ${s} ${b.ticket_item_border_color}`;
  } else if (b.ticket_item_border_style === "none") {
    vars["--ticket-border"] = "none";
  }
  if (has(b.ticket_item_border_radius)) vars["--ticket-radius"] = `${b.ticket_item_border_radius}px`;
  if (has(b.ticket_name_color)) vars["--ticket-name-color"] = b.ticket_name_color;
  if (has(b.ticket_price_color)) vars["--ticket-price-color"] = b.ticket_price_color;

  // ── Ticket/checkout flow box: container styling + heading font (Update 28) ──
  if (has(b.ticket_heading_font)) { vars["--ticket-heading-font"] = fontStack(b.ticket_heading_font); fonts.push(b.ticket_heading_font); }
  if (has(b.timeslot_heading_font)) { vars["--timeslot-heading-font"] = fontStack(b.timeslot_heading_font); fonts.push(b.timeslot_heading_font); }
  if (has(b.ticket_box_bg) || has(b.ticket_box_bg_opacity)) {
    const v = hexToRgba(b.ticket_box_bg || "#000000", b.ticket_box_bg_opacity ?? 100);
    if (v) vars["--flow-box-bg"] = v;
  }
  if (b.ticket_box_border_style && b.ticket_box_border_style !== "none") {
    const w = has(b.ticket_box_border_width) ? b.ticket_box_border_width : 1;
    const c = b.ticket_box_border_color || "#dc2626";
    vars["--flow-box-border"] = `${w}px ${b.ticket_box_border_style} ${c}`;
  } else if (b.ticket_box_border_style === "none") {
    vars["--flow-box-border"] = "none";
  }
  if (has(b.ticket_box_radius)) vars["--flow-box-radius"] = `${b.ticket_box_radius}px`;
  if (has(b.ticket_box_padding)) vars["--flow-box-padding"] = `${b.ticket_box_padding}px`;

  // ── Confirmation page (Update 20 — null inherits panel/calendar-box defaults) ──
  if (has(b.confirmation_heading_size)) vars["--confirmation-heading-size"] = `${b.confirmation_heading_size}px`;
  if (has(b.confirmation_heading_weight)) vars["--confirmation-heading-weight"] = String(b.confirmation_heading_weight);
  if (has(b.confirmation_heading_color)) vars["--confirmation-heading-color"] = b.confirmation_heading_color;
  if (has(b.confirmation_heading_align)) vars["--confirmation-heading-align"] = b.confirmation_heading_align;
  if (has(b.confirmation_heading_font)) { vars["--confirmation-heading-font"] = fontStack(b.confirmation_heading_font); fonts.push(b.confirmation_heading_font); }
  if (has(b.confirmation_subheading_color)) vars["--confirmation-subheading-color"] = b.confirmation_subheading_color;
  // order summary box
  if (has(b.confirmation_order_box_bg)) { const v = hexToRgba(b.confirmation_order_box_bg, b.confirmation_order_box_bg_opacity); if (v) vars["--confirmation-order-bg"] = v; }
  if (has(b.confirmation_order_box_border_color)) {
    const w = has(b.confirmation_order_box_border_width) ? b.confirmation_order_box_border_width : 1;
    const s = b.confirmation_order_box_border_style || "solid";
    vars["--confirmation-order-border"] = `${w}px ${s} ${b.confirmation_order_box_border_color}`;
  } else if (b.confirmation_order_box_border_style === "none") {
    vars["--confirmation-order-border"] = "none";
  }
  if (has(b.confirmation_order_box_border_radius)) vars["--confirmation-order-radius"] = `${b.confirmation_order_box_border_radius}px`;
  if (has(b.confirmation_order_box_padding)) vars["--confirmation-order-padding"] = `${b.confirmation_order_box_padding}px`;
  // ticket list box
  if (has(b.confirmation_ticket_box_bg)) { const v = hexToRgba(b.confirmation_ticket_box_bg, b.confirmation_ticket_box_bg_opacity); if (v) vars["--confirmation-ticket-bg"] = v; }
  if (has(b.confirmation_ticket_box_border_color)) {
    const w = has(b.confirmation_ticket_box_border_width) ? b.confirmation_ticket_box_border_width : 1;
    const s = b.confirmation_ticket_box_border_style || "solid";
    vars["--confirmation-ticket-border"] = `${w}px ${s} ${b.confirmation_ticket_box_border_color}`;
  } else if (b.confirmation_ticket_box_border_style === "none") {
    vars["--confirmation-ticket-border"] = "none";
  }
  if (has(b.confirmation_ticket_box_border_radius)) vars["--confirmation-ticket-radius"] = `${b.confirmation_ticket_box_border_radius}px`;
  if (has(b.confirmation_ticket_box_padding)) vars["--confirmation-ticket-padding"] = `${b.confirmation_ticket_box_padding}px`;

  return { vars, fonts };
}
